import styled from "styled-components";
import useZillowImport from "../../hooks/useZillowImport";
import { importPhotoCounts } from "../../utils/sourceCoverage";
import { isListingImportActive } from "../../utils/zillowImportSession";
import PhotoImportCoverage from "./PhotoImportCoverage";

const Panel = styled.section`
  padding: 24px;
  margin: 18px 0;
  background: #f5f9f8;
  border: 1px solid #d7e7e4;
  border-radius: 12px;
  h2 { font-size: 1.4rem; }
  button { margin: 8px 10px 0 0; padding: 10px 16px; }
  img { width: 80px; height: 60px; object-fit: cover; margin: 4px; border-radius: 4px; }
`;

/** @param {{importer: ReturnType<typeof useZillowImport>}} props */
export const ImportProgressPanel = ({ importer }) => {
  const data = importer.snapshot;
  const status = data?.import?.status;
  const active = isListingImportActive(data);
  const counts = importPhotoCounts(data?.import);
  const total = counts?.selected || 0;
  const completed = counts?.saved || 0;
  const failed = counts?.failed || 0;
  const titles = {
    queued: "Your listing import is queued",
    processing: "Importing your listing",
    partial: "Some listing photos could not be imported",
    failed: "The listing import needs attention",
    ready: "Your listing details are ready",
    cancelled: "Import stopped — your existing details are kept",
  };
  const title =
    titles[/** @type {keyof typeof titles} */ (status)] ||
    (importer.busy ? "Starting your listing import" : "Resume your saved listing import");
  return (
    <Panel aria-label="Listing import progress">
      <h2>{title}</h2>
      <output aria-live="polite">
        {data?.address && <p>{data.address}</p>}
        {active && (
          <p>
            {data.import.stage === "photos"
              ? "Saving listing photos…"
              : "Retrieving property details…"}
          </p>
        )}
        {total > 0 && (
          <p>
            {completed} of {total} photos saved
            {failed > 0 ? `; ${failed} could not be imported` : ""}.
          </p>
        )}
        {importer.checking && <p>Checking progress…</p>}
      </output>
      <PhotoImportCoverage importState={data?.import} />
      {Array.isArray(data?.images) && data.images.length > 0 && (
        <div aria-label="Photos already imported">
          {data.images.slice(0, 6).map((/** @type {any} */ photo, /** @type {number} */ index) => (
            <img
              key={photo.id || photo.file || index}
              src={photo.file || photo.url}
              alt={`Imported listing view ${index + 1}`}
            />
          ))}
        </div>
      )}
      {importer.recoveryWarning && <p role="alert">{importer.recoveryWarning}</p>}
      {(importer.error || data?.import?.error_message) && (
        <p role="alert">{importer.error || data.import.error_message}</p>
      )}
      <p>Your imported details and saved photos are kept. No credits are spent on this step.</p>
      {data?.import?.retryable && !active && (
        <button
          type="button"
          disabled={importer.busy || importer.cooldown.blocked}
          onClick={importer.retry}
        >
          Retry missing details or photos
        </button>
      )}
      <button
        type="button"
        disabled={
          importer.busy ||
          importer.checking ||
          (importer.uuid ? importer.statusCheckingBlocked : importer.cooldown.blocked)
        }
        onClick={importer.resume}
      >
        {importer.uuid ? "Check progress again" : "Resume import"}
      </button>
      {importer.uuid ? (
        <button type="button" disabled={importer.busy} onClick={importer.continueManually}>
          {importer.busy ? "Please wait…" : "Continue with details manually"}
        </button>
      ) : (
        importer.discard && (
          <button type="button" disabled={importer.busy} onClick={importer.discard}>
            Start a different property
          </button>
        )
      )}
    </Panel>
  );
};

/** The confirmation route uses the same recovery UI for an active imported listing. */
/** @param {{listingUuid: string}} props */
const ListingImportProgress = ({ listingUuid }) => {
  const importer = useZillowImport({ listingUuid });
  return <ImportProgressPanel importer={importer} />;
};

export default ListingImportProgress;
